import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Banner, FormField } from '@/components/AdminPanel/AdminPrimitives';
import { LCBtn, LCIcon, CLS } from '@/components/common/LCPrimitives';
import apiService from '@/services/apiService';
import { useAuth } from '@/contexts/AuthContext';

interface MarginTradingOverride {
  portfolio_id_lig: string;
  override_status: 'TRUE' | 'FALSE' | 'AUTO';
  override_value: boolean | null;
  notes: string | null;
  overridden_by: string;
  overridden_at: string;
}

interface PortfolioWithMargin {
  portfolio_id_lig: string;
  client_name: string;
  is_margin_trading: boolean | null;
  system_is_margin_trading: boolean | null;
  has_margin_account: boolean | null;
}

const MarginTradingOverridesTab: React.FC = () => {
  const { user } = useAuth();
  const [portfolios, setPortfolios] = useState<PortfolioWithMargin[]>([]);
  const [overrides, setOverrides] = useState<Map<string, MarginTradingOverride>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioWithMargin | null>(null);
  const [overrideStatus, setOverrideStatus] = useState<'TRUE' | 'FALSE' | 'AUTO'>('AUTO');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const portfoliosData = await apiService.getPortfolios();
      setPortfolios(portfoliosData.filter((p: any) => p.has_margin_account === true));
      const overridesData = await apiService.getMarginTradingOverrides();
      const map = new Map<string, MarginTradingOverride>();
      overridesData.forEach((o: MarginTradingOverride) => map.set(o.portfolio_id_lig, o));
      setOverrides(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenDialog = (portfolio: PortfolioWithMargin) => {
    setSelectedPortfolio(portfolio);
    const existing = overrides.get(portfolio.portfolio_id_lig);
    setOverrideStatus(existing?.override_status || 'AUTO');
    setNotes(existing?.notes || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false); setSelectedPortfolio(null);
    setOverrideStatus('AUTO'); setNotes('');
  };

  const handleSaveOverride = async () => {
    if (!selectedPortfolio || !user) return;
    setSaving(true);
    try {
      await apiService.setMarginTradingOverride({
        portfolio_id_lig: selectedPortfolio.portfolio_id_lig,
        override_status: overrideStatus,
        notes: notes || undefined,
        overridden_by: user.email,
      });
      await fetchData(); handleCloseDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save override');
    } finally { setSaving(false); }
  };

  const handleDeleteOverride = async (portfolioId: string) => {
    if (!window.confirm('Remove this override?')) return;
    try {
      await apiService.deleteMarginTradingOverride(portfolioId);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete override');
    }
  };

  const getStatusBadge = (portfolio: PortfolioWithMargin) => {
    const override = overrides.get(portfolio.portfolio_id_lig);
    if (override) {
      if (override.override_status === 'AUTO') {
        return (
          <span className="text-[11px] font-semibold text-[#555] bg-black/[0.05] px-2 py-0.5">
            AUTO ({portfolio.is_margin_trading ? 'YES' : 'NO'})
          </span>
        );
      }
      const isOverridden = override.override_value;
      return (
        <div className="flex items-center gap-1.5">
          <span
            style={{
              color: isOverridden ? '#b6770b' : '#1f8a4a',
              background: isOverridden ? 'rgba(255,191,0,0.12)' : 'rgba(31,138,74,0.10)',
              border: `1px solid ${isOverridden ? 'rgba(182,119,11,0.30)' : 'rgba(31,138,74,0.30)'}`,
            }}
            className="inline-flex items-center px-[7px] py-[2px] text-[9.5px] font-bold tracking-[0.06em] uppercase whitespace-nowrap"
          >
            {isOverridden ? 'YES (Manual)' : 'NO (Manual)'}
          </span>
          <span className="text-[10px] text-[#7A7A76]">
            (Sys: {portfolio.system_is_margin_trading ? 'YES' : 'NO'})
          </span>
        </div>
      );
    }
    const v = portfolio.is_margin_trading;
    return (
      <span
        style={{
          color: v ? '#b6770b' : '#7A7A76',
          background: v ? 'rgba(255,191,0,0.12)' : 'transparent',
          border: `1px solid ${v ? 'rgba(182,119,11,0.30)' : 'rgba(0,0,0,0.15)'}`,
        }}
        className="inline-flex items-center px-[7px] py-[2px] text-[9.5px] font-bold tracking-[0.06em] uppercase whitespace-nowrap"
      >
        {v ? 'YES' : 'NO'}
      </span>
    );
  };

  const getOverrideBadge = (portfolio: PortfolioWithMargin) => {
    const override = overrides.get(portfolio.portfolio_id_lig);
    if (!override) {
      return <span className="text-[11px] text-[#7A7A76]">NONE</span>;
    }
    const isManual = override.override_status !== 'AUTO';
    return (
      <span
        style={{
          color: isManual ? '#6464E6' : '#555',
          background: isManual ? 'rgba(100,100,230,0.08)' : 'rgba(0,0,0,0.04)',
          border: `1px solid ${isManual ? 'rgba(100,100,230,0.25)' : 'rgba(0,0,0,0.15)'}`,
        }}
        className="inline-flex items-center px-[7px] py-[2px] text-[9.5px] font-bold tracking-[0.06em] uppercase whitespace-nowrap"
      >
        {override.override_status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] text-[#7A7A76] text-[13px]">
        Loading…
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        {/* <div>
          <h2 className="font-serif text-[22px] font-normal m-0 mb-1">Margin Trading Overrides</h2>
          <p className="text-[12.5px] text-[#7A7A76] m-0">
            Manually override automated margin trading detection for specific portfolios.
          </p>
        </div> */}
        <LCBtn variant="outline" size="sm" onClick={fetchData}>
          <LCIcon name="refresh" size={12} /> Refresh
        </LCBtn>
      </div>

      {error && <Banner variant="error" onClose={() => setError(null)} className="mb-3">{error}</Banner>}

      <Banner variant="info" className="mb-4">
        <strong>Override Modes: </strong>
        <strong>TRUE</strong> — Force margin trading = YES;{' '}
        <strong>FALSE</strong> — Force margin trading = NO;{' '}
        <strong>AUTO</strong> — Use system automatic detection (no override)
      </Banner>

      <div className={CLS.tableWrap}>
        <table className={CLS.table}>
          <thead>
            <tr>
              <th className={CLS.th}>Portfolio ID</th>
              <th className={CLS.th}>Client Name</th>
              <th className={CLS.th}>Margin Trading</th>
              <th className={CLS.th}>Override Mode</th>
              <th className={CLS.th}>Overridden By</th>
              <th className={CLS.th}>Override Date</th>
              <th className={CLS.th}>Notes</th>
              <th className={`${CLS.th} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {portfolios.length === 0 ? (
              <tr>
                <td colSpan={8} className={`${CLS.td} ${CLS.muted} text-center py-8`}>
                  No portfolios with margin accounts found.
                </td>
              </tr>
            ) : (
              portfolios.map(portfolio => {
                const override = overrides.get(portfolio.portfolio_id_lig);
                return (
                  <tr key={portfolio.portfolio_id_lig} className="hover:bg-[#FAF9F8] transition-colors">
                    <td className={`${CLS.td} ${CLS.tdMono}`}>{portfolio.portfolio_id_lig}</td>
                    <td className={CLS.td}>{portfolio.client_name}</td>
                    <td className={CLS.td}>{getStatusBadge(portfolio)}</td>
                    <td className={CLS.td}>{getOverrideBadge(portfolio)}</td>
                    <td className={CLS.td}>{override?.overridden_by || '—'}</td>
                    <td className={CLS.td}>
                      {override ? new Date(override.overridden_at).toLocaleString() : '—'}
                    </td>
                    <td className={`${CLS.td} max-w-[200px]`}>
                      <span className="block truncate text-[12px] text-[#555]">{override?.notes || '—'}</span>
                    </td>
                    <td className={`${CLS.td} text-right`}>
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          className={CLS.iconBtn}
                          onClick={() => handleOpenDialog(portfolio)}
                          title="Set Override"
                        >
                          <LCIcon name="edit" size={13} />
                        </button>
                        {override && override.override_status !== 'AUTO' && (
                          <button
                            className={`${CLS.iconBtn} hover:text-[#E50025]`}
                            onClick={() => handleDeleteOverride(portfolio.portfolio_id_lig)}
                            title="Remove Override"
                          >
                            <LCIcon name="trash" size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={selectedPortfolio ? `Override — ${selectedPortfolio.portfolio_id_lig}` : 'Set Override'}
        size="md"
        footer={
          <>
            <LCBtn variant="outline" size="sm" onClick={handleCloseDialog} disabled={saving}>Cancel</LCBtn>
            <LCBtn variant="primary" size="sm" onClick={handleSaveOverride} disabled={saving}>
              {saving ? 'Saving…' : 'Save Override'}
            </LCBtn>
          </>
        }
      >
        {selectedPortfolio && (
          <div className="flex flex-col gap-3.5">
            <Banner variant="info" className="">
              <strong>Client:</strong> {selectedPortfolio.client_name}<br />
              <strong>System Detection:</strong>{' '}
              {selectedPortfolio.is_margin_trading ? 'YES (Margin Trading)' : 'NO (Not Margin Trading)'}
            </Banner>

            <FormField label="Override Mode">
              <select
                className={CLS.input}
                value={overrideStatus}
                onChange={e => setOverrideStatus(e.target.value as 'TRUE' | 'FALSE' | 'AUTO')}
              >
                <option value="AUTO">AUTO (Use System Detection)</option>
                <option value="TRUE">TRUE (Force Margin Trading = YES)</option>
                <option value="FALSE">FALSE (Force Margin Trading = NO)</option>
              </select>
            </FormField>

            <FormField label="Notes" hint="Optional — explain why this override is needed">
              <textarea
                className={`${CLS.input} min-h-[80px] resize-y`}
                placeholder="Explain why this override is needed…"
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </FormField>

            <Banner variant="info" className="">
              <strong>Overridden by:</strong> {user?.email || 'Unknown user'}
            </Banner>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MarginTradingOverridesTab;
